/**
 * The Lagezuordnung of a Befund description, as one short line: "Bef. 7000,
 * Fl. 1, Pl. 1" ("…, Pr. 2" for a Profil, "…, Pl. 1, Teil A" for one of
 * several descriptions of the same Planum). ONE grammar, used wherever a
 * description is named in passing. Abbreviated on purpose: the long form
 * ("7000; Fläche 1; Planum 1") wrapped or was cut off in chips and cells.
 */

export interface LocationParts {
    /** The Befundnummer. Always shown — as an em dash when it is unknown. */
    findingNumber: string;
    /** Fläche, omitted when blank. */
    area?: string;
    /** Planumnummer — set this OR `profileNumber`, not both. */
    planumNumber?: string;
    /** Profilnummer — set this OR `planumNumber`, not both. */
    profileNumber?: string;
    /**
     * The Teil mark, when the description is one of several of the same
     * Planum/Profil. Comes LAST because it narrows what precedes it.
     */
    part?: string;
}

/**
 * "Bef. 7000, Fl. 1, Pl. 1". A part with no value is left out entirely, so a
 * whole-Befund reference reads simply "Bef. 7000".
 */
export function locationLabel({
    findingNumber,
    area,
    planumNumber,
    profileNumber,
    part
}: LocationParts): string {
    const parts = [`Bef. ${findingNumber.trim() || '—'}`];
    if (area?.trim()) {
        parts.push(`Fl. ${area.trim()}`);
    }
    if (planumNumber?.trim()) {
        parts.push(`Pl. ${planumNumber.trim()}`);
    } else if (profileNumber?.trim()) {
        parts.push(`Pr. ${profileNumber.trim()}`);
    }
    // Only ever after a Planum/Profil: a Teil of nothing says nothing.
    if (part?.trim() && (planumNumber?.trim() || profileNumber?.trim())) {
        parts.push(`Teil ${part.trim()}`);
    }
    return parts.join(', ');
}

/**
 * A Lage line split around its Befundnummer, so a renderer can set that part
 * apart without knowing the grammar. A label this cannot read (free text typed
 * into a picker) comes back with an empty `number` and the whole string as
 * `after`, so anything can be passed through and rendered unconditionally.
 */
export function splitLocationLabel(label: string): {
    /** The "Bef. " marker, kept out of the emphasis. */
    before: string;
    /** The Befundnummer, or '' when the label is not a Lage line. */
    number: string;
    /** ", Fl. 1, Pl. 1" — the rest, unchanged. */
    after: string;
} {
    // The same grammar `locationLabel` writes: number runs to the first comma.
    const match = /^(Bef\.\s*)([^,]*)(.*)$/s.exec(label.trim());
    if (!match) {
        return { before: '', number: '', after: label };
    }
    return { before: match[1], number: match[2].trim(), after: match[3] };
}

/**
 * The inverse: the Befundnummer out of a Lage line. Anything that isn't one
 * comes back as itself, so every value can be run through unchecked.
 *
 * The stratigraphy fields store the BARE Befundnummer (the ExcaBook wire format
 * splits on ','/';' and reads each token as one) while their pickers offer the
 * full Lage line — this is what keeps the location out of the record.
 */
export function locationFindingNumber(label: string): string {
    const trimmed = label.trim();
    // 'Bef. 7000, Fl. 1, Pl. 1' — the number runs to the first comma.
    const match = /^Bef\.\s*([^,]*)/.exec(trimmed);
    if (match) {
        return match[1].trim();
    }
    // The superseded '7000; Fläche 1; Planum 1' form, and bare free text.
    return trimmed.split(';')[0].trim();
}
